import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const video1Url = 'https://storage.readdy-site.link/project_files/4ae51fa3-40da-40a1-b133-2a5cd9cb4858/84fd7527-dab3-4568-a100-d4a92ae13da6_Joviaa-1.mp4';
  const video2Url = 'https://storage.readdy-site.link/project_files/4ae51fa3-40da-40a1-b133-2a5cd9cb4858/35492388-96e2-4fcc-b994-909c773a4a5f_joviaa-2.mp4';

  return (
    <section className="relative w-full overflow-hidden bg-gradient-to-br from-background-50 via-background-50 to-secondary-200">
      {/* Floating decorative background blobs */}
      <style>{`
        @keyframes floatA {
          0%, 100% { transform: translate(0, 0); }
          33% { transform: translate(18px, -14px); }
          66% { transform: translate(-10px, 10px); }
        }
        @keyframes floatB {
          0%, 100% { transform: translate(0, 0); }
          33% { transform: translate(-16px, 12px); }
          66% { transform: translate(12px, -8px); }
        }
        @keyframes floatC {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(6px, -10px) scale(1.06); }
        }
        @keyframes driftX {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(-16px); }
        }
        @keyframes pulseDot {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.4); }
        }
      `}</style>

      {/* Large gold blob — top-right, behind videos */}
      <div
        className="absolute -top-20 -right-16 w-[420px] h-[420px] md:w-[560px] md:h-[560px] rounded-full bg-accent-500/8 blur-[100px] pointer-events-none"
        style={{ animation: 'floatA 14s ease-in-out infinite' }}
        aria-hidden="true"
      />
      {/* Small navy circle — mid-left, behind text */}
      <div
        className="absolute top-1/4 -left-10 w-[280px] h-[280px] md:w-[360px] md:h-[360px] rounded-full bg-primary-500/6 blur-[80px] pointer-events-none"
        style={{ animation: 'floatB 18s ease-in-out infinite', animationDelay: '2s' }}
        aria-hidden="true"
      />
      {/* Tiny gold accent dot cluster — bottom-center */}
      <div
        className="absolute bottom-8 left-1/2 w-2 h-2 rounded-full bg-accent-500/25 pointer-events-none"
        style={{ animation: 'pulseDot 5s ease-in-out infinite, driftX 10s ease-in-out infinite' }}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-10 left-[48%] w-1.5 h-1.5 rounded-full bg-accent-500/15 pointer-events-none"
        style={{ animation: 'pulseDot 6s ease-in-out infinite 1.2s, driftX 12s ease-in-out infinite 0.8s' }}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-6 left-[52%] w-1 h-1 rounded-full bg-accent-500/20 pointer-events-none"
        style={{ animation: 'pulseDot 4.5s ease-in-out infinite 0.6s, driftX 9s ease-in-out infinite 1.5s' }}
        aria-hidden="true"
      />
      {/* Soft warm oval — upper-center, bridging gap */}
      <div
        className="absolute top-10 left-1/2 w-[500px] h-[200px] rounded-[50%] bg-secondary-500/5 blur-[90px] pointer-events-none"
        style={{ animation: 'driftX 16s ease-in-out infinite', animationDelay: '3s' }}
        aria-hidden="true"
      />
      {/* Extra accent blob — bottom-right, behind videos */}
      <div
        className="absolute -bottom-24 -right-24 w-[300px] h-[300px] md:w-[420px] md:h-[420px] rounded-full bg-accent-500/5 blur-[90px] pointer-events-none"
        style={{ animation: 'floatC 12s ease-in-out infinite', animationDelay: '4s' }}
        aria-hidden="true"
      />

      <div className="relative z-10 max-w-none mx-auto px-4 md:px-6 pt-12 pb-16 md:pt-16 md:pb-20">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Left: Text content */}
          <div className="flex-1 w-full text-center lg:text-left">
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-heading font-bold text-primary-500 leading-[1.05] tracking-tight">
              Built to Last.
              <br />
              Made to Shine.
            </h1>
            <p className="mt-4 md:mt-5 text-base md:text-lg text-foreground-600 font-label max-w-lg lg:max-w-none mx-auto lg:mx-0">
              Premium PVD gold plated&nbsp; stainless steel jewellery, engineered to survive real life.
            </p>
            <div className="mt-7 md:mt-8 flex flex-col sm:flex-row items-center gap-3 justify-center lg:justify-start">
              <Link
                to="/shop"
                className="px-8 py-3.5 bg-accent-500 text-primary-800 rounded-full font-heading font-semibold text-sm md:text-base hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Shop New Arrivals
              </Link>
              <Link
                to="/durability"
                className="px-8 py-3.5 border-2 border-primary-500 text-primary-500 rounded-full font-heading font-semibold text-sm md:text-base hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Discover the Difference
              </Link>
            </div>
          </div>

          {/* Right: Videos */}
          <div className="flex-1 w-full">
            {isMobile ? (
              /* Mobile: single video — muted, no controls */
              <div className="relative aspect-[4/5] max-w-sm mx-auto rounded-2xl overflow-hidden">
                <video
                  src={video2Url}
                  autoPlay
                  loop
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              /* Desktop: two equal-sized videos side by side — muted, no controls */
              <div className="flex items-stretch gap-4 md:gap-5 max-w-lg mx-auto lg:max-w-none">
                {/* Video 1 — muted, no controls */}
                <div className="relative flex-1 aspect-[4/5] rounded-2xl overflow-hidden -rotate-1 hover:rotate-0 transition-transform duration-500">
                  <video
                    src={video1Url}
                    autoPlay
                    loop
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Video 2 — muted, no controls */}
                <div className="relative flex-1 aspect-[4/5] rounded-2xl overflow-hidden rotate-1 hover:rotate-0 transition-transform duration-500">
                  <video
                    src={video2Url}
                    autoPlay
                    loop
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}